import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ProductCard from '../../components/shop/ProductCard';
import { useSettings } from '../../context/SettingsContext';

export default function HomePage() {
  const { settings } = useSettings();
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    axios.get('/api/categories').then(r => setCategories(r.data)).catch(() => {});
    axios.get('/api/products?featured=1&limit=4').then(r => setFeatured(r.data.products || [])).catch(() => {});
    axios.get('/api/products?new_arrivals=1&limit=8').then(r => setNewArrivals(r.data.products || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (search.trim()) navigate(`/shop?search=${encodeURIComponent(search.trim())}`);
  };

  return (
    <div>
      {/* Hero */}
      <section style={{ background: 'linear-gradient(135deg, #111b33 0%, #1a2744 60%, #243660 100%)', color: '#fff', padding: '72px 0 80px', position: 'relative', overflow: 'hidden' }}>
        {/* Decorative gold line */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: 'linear-gradient(90deg, #c9a227, #e8c547, #c9a227)' }} />
        <div className="container" style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(201,162,39,0.15)', border: '1px solid rgba(201,162,39,0.3)', borderRadius: 20, padding: '6px 16px', marginBottom: 24, fontSize: 13, color: '#c9a227', fontWeight: 700 }}>
            🏆 Same Great Service for {settings.years_in_business || '15'} Years
          </div>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 52px)', fontWeight: 900, lineHeight: 1.15, marginBottom: 16 }}>
            Natchitoches' Trusted<br />
            <span style={{ color: '#c9a227' }}>Pawn Shop & Tech Repair</span>
          </h1>
          <p style={{ fontSize: 17, color: 'rgba(255,255,255,0.75)', maxWidth: 560, margin: '0 auto 36px', lineHeight: 1.7 }}>
            Browse thousands of items — guns, electronics, jewelry, tools and more. Shop online or visit us at {settings.store_address}.
          </p>
          {/* Search bar */}
          <form onSubmit={handleSearch} style={{ display: 'flex', maxWidth: 540, margin: '0 auto 36px', background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.25)' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search our inventory..."
              style={{ flex: 1, padding: '16px 20px', border: 'none', outline: 'none', fontSize: 15, color: '#1a2744' }} />
            <button type="submit" style={{ padding: '16px 28px', background: '#c9a227', border: 'none', color: '#1a2744', fontWeight: 800, fontSize: 15, cursor: 'pointer', transition: 'background 0.15s' }}>
              Search
            </button>
          </form>
          <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/shop" className="btn btn-primary btn-lg">Browse All Inventory</Link>
            <Link to="/sell" className="btn btn-outline-gold btn-lg" style={{ borderColor: 'rgba(201,162,39,0.6)', color: '#c9a227' }}>Sell or Pawn →</Link>
          </div>
        </div>
      </section>

      {/* Category grid */}
      <section style={{ background: '#fff', padding: '52px 0' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: 36 }}>
            <h2 className="section-title">Shop by Category</h2>
            <div className="gold-divider gold-divider-center" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 14 }}>
            {categories.map(cat => (
              <Link key={cat.slug} to={`/shop/${cat.slug}`}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '24px 12px', background: '#f8f7f2', borderRadius: 12, border: '2px solid transparent', transition: 'all 0.15s', textAlign: 'center' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = '#c9a227'; e.currentTarget.style.background = 'rgba(201,162,39,0.05)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'transparent'; e.currentTarget.style.background = '#f8f7f2'; e.currentTarget.style.transform = 'none'; }}>
                <span style={{ fontSize: 32 }}>{cat.icon}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: '#1a2744', lineHeight: 1.3 }}>{cat.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* New arrivals */}
      {newArrivals.length > 0 && (
        <section className="section" style={{ background: '#f8f7f2' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
              <div>
                <h2 className="section-title">New Arrivals</h2>
                <div className="gold-divider" />
              </div>
              <Link to="/shop?new_arrivals=1" className="btn btn-outline btn-sm">View All →</Link>
            </div>
            <div className="grid-auto">
              {newArrivals.map(p => <ProductCard key={p.uuid} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Featured */}
      {featured.length > 0 && (
        <section className="section" style={{ background: '#fff' }}>
          <div className="container">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28 }}>
              <div>
                <h2 className="section-title">Featured Items</h2>
                <div className="gold-divider" />
              </div>
              <Link to="/shop?featured=1" className="btn btn-outline btn-sm">View All →</Link>
            </div>
            <div className="grid-4">
              {featured.map(p => <ProductCard key={p.uuid} product={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* Value props */}
      <section className="section" style={{ background: '#1a2744', color: '#fff' }}>
        <div className="container">
          <div className="grid-4" style={{ gap: 32, textAlign: 'center' }}>
            {[
              ['🛡️', 'FFL Licensed Dealer', 'All firearm sales handled legally with full compliance.'],
              ['🔧', 'Expert Tech Repair', 'Phones, tablets, laptops, and more. Fast turnaround.'],
              ['💰', 'Best Prices', 'Fair offers on everything. We buy, sell, and loan.'],
              ['📍', '15 Years in Natchitoches', 'A trusted community business.'],
            ].map(([icon, title, desc]) => (
              <div key={title}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>{icon}</div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#c9a227', marginBottom: 8 }}>{title}</h3>
                <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Home page intro section — title/text editable in Admin → Settings */}
      <section className="section" style={{ background: '#fff' }}>
        <div className="container">
          <div style={{ maxWidth: 760, margin: '0 auto', textAlign: 'center' }}>
            <h2 className="section-title">{settings.home_section_title}</h2>
            <div className="gold-divider gold-divider-center" />
            {settings.home_section_body && (
              <p style={{ fontSize: 16, color: '#555', lineHeight: 1.8, marginBottom: 28, whiteSpace: 'pre-line' }}>
                {settings.home_section_body}
              </p>
            )}
            <div style={{ display: 'flex', gap: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/about" className="btn btn-navy">Meet the Team</Link>
              <Link to="/contact" className="btn btn-outline">Get in Touch</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Sell/pawn CTA */}
      <section style={{ background: 'linear-gradient(135deg, #c9a227 0%, #e8c547 100%)', padding: '52px 0' }}>
        <div className="container" style={{ textAlign: 'center' }}>
          <h2 style={{ fontSize: 28, fontWeight: 900, color: '#1a2744', marginBottom: 12 }}>Have Something to Sell or Pawn?</h2>
          <p style={{ fontSize: 16, color: 'rgba(26,39,68,0.75)', marginBottom: 28, maxWidth: 480, margin: '0 auto 28px' }}>
            We buy and accept as collateral: guns, jewelry, electronics, tools, and much more. Get a quote today.
          </p>
          <Link to="/sell" className="btn btn-navy btn-lg">Get a Quote →</Link>
        </div>
      </section>
    </div>
  );
}
